export interface SavedTest {
    id: string
    createdAt: number
}

export const SAVED_KEY = 'dlt.v1.savedTests'
const MAX_SAVED = 20

/**
 * localStorage rather than cookies.
 *
 * The old client keyed its cookie on `{userIP}/{ISO date}`, which meant the
 * visitor's own IP address was sent to the server in a Cookie header on every
 * single request -- from a site whose subject is DNS privacy. It also stored the
 * full API URL as the value; only the id is kept here, and the URL is built in
 * code, so a tampered store cannot point a fetch anywhere it likes.
 */
export function readSavedTests(): SavedTest[] {
    try {
        const raw = localStorage.getItem(SAVED_KEY)
        if (!raw) return []
        const parsed: unknown = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        // validate on read: corrupt or hand-edited data degrades to empty
        return parsed.filter((t): t is SavedTest =>
            typeof t === 'object' && t !== null &&
            typeof (t as SavedTest).id === 'string' &&
            typeof (t as SavedTest).createdAt === 'number')
    } catch {
        return []
    }
}

/*
 * Saving happens in ShareBar and the list is rendered elsewhere, so writes are
 * announced rather than passed down. useSyncExternalStore wants a stable
 * snapshot, so the last read is cached and only replaced on write -- returning a
 * fresh array from every read would re-render forever.
 */
type Listener = () => void
const listeners = new Set<Listener>()
let snapshot: SavedTest[] | undefined

export function subscribeSavedTests(listener: Listener): () => void {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
}

export function savedTestsSnapshot(): SavedTest[] {
    if (!snapshot) snapshot = readSavedTests()
    return snapshot
}

/* Another tab -- or the browser's own "clear site data" -- can change the store
   underneath us. `storage` only fires in *other* tabs, so this never double-fires
   for our own writes. */
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key !== null && event.key !== SAVED_KEY) return
        snapshot = undefined
        for (const listener of listeners) listener()
    })
}

function publish(next: SavedTest[]): SavedTest[] {
    snapshot = next
    for (const listener of listeners) listener()
    return next
}

export function saveTest(id: string, createdAt = Date.now()): SavedTest[] {
    const next = [{ id, createdAt }, ...readSavedTests().filter((t) => t.id !== id)]
        .slice(0, MAX_SAVED)
    try {
        localStorage.setItem(SAVED_KEY, JSON.stringify(next))
    } catch {
        // private mode; the list simply will not persist this session
    }
    return publish(next)
}

export function removeSavedTest(id: string): SavedTest[] {
    const next = readSavedTests().filter((t) => t.id !== id)
    try {
        localStorage.setItem(SAVED_KEY, JSON.stringify(next))
    } catch { /* as above */ }
    return publish(next)
}
